import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Agent } from '@atproto/api'
import { useAuth } from '@/hooks/useAuth'
import {
    fetchFavoriteAccounts,
    addFavoriteAccount,
    removeFavoriteAccount,
    type FavoriteAccount,
} from '@/services/favoriteAccountsService'

/**
 * Manages the authenticated user's favorited accounts.
 *
 * - Fetches favorites from the Mackinac XRPC server (AT Protocol only)
 * - Exposes a Set of favorited DIDs for O(1) lookup
 * - Provides toggle mutation with optimistic updates
 * - Only fetches when there is an active AT Protocol session
 */
export function useFavoriteAccounts() {
    const { session } = useAuth()
    const queryClient = useQueryClient()
    const agent = useMemo(() => (session ? new Agent(session) : null), [session])

    const queryKey = ['favorite-accounts', session?.did] as const

    const {
        data: accounts = [],
        isLoading,
        refetch,
    } = useQuery<FavoriteAccount[], Error>({
        queryKey,
        queryFn: () => fetchFavoriteAccounts(agent!),
        enabled: !!agent,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 1,
    })

    // Set of favorited DIDs for O(1) isFavorite checks
    const favoritedDids = useMemo(() => new Set(accounts.map(a => a.did)), [accounts])

    const toggleFavorite = useMutation({
        mutationFn: async ({ did, wasFavorited }: { did: string; wasFavorited: boolean }) => {
            if (!agent) throw new Error('Not authenticated')
            if (wasFavorited) {
                await removeFavoriteAccount(agent, did)
            } else {
                await addFavoriteAccount(agent, did)
            }
        },
        onMutate: async ({ did, wasFavorited }) => {
            await queryClient.cancelQueries({ queryKey })
            const previous = queryClient.getQueryData<FavoriteAccount[]>(queryKey)

            queryClient.setQueryData<FavoriteAccount[]>(queryKey, old => {
                const current = old ?? []
                if (wasFavorited) {
                    // Remove from list
                    return current.filter(a => a.did !== did)
                } else {
                    // Add to top of list (most-recently-added first)
                    const filtered = current.filter(a => a.did !== did)
                    return [{ did, favoritedAt: new Date().toISOString() }, ...filtered]
                }
            })

            return { previous }
        },
        onError: (_err, _vars, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData<FavoriteAccount[]>(queryKey, context.previous)
            }
        },
        onSettled: () => {
            // Sync with server after mutation settles
            void queryClient.invalidateQueries({ queryKey })
        },
    })

    return {
        accounts,
        favoritedDids,
        isLoading,
        refetch,
        isEnabled: !!agent,
        toggleFavorite: (did: string) => {
            toggleFavorite.mutate({ did, wasFavorited: favoritedDids.has(did) })
        },
        isFavorite: (did: string) => favoritedDids.has(did),
        isToggling: toggleFavorite.isPending,
    }
}
